import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay/classic';
import { Link } from 'react-router';
import classNames from 'classnames';

import Emojify from 'app/components/shared/Emojify';
import Panel from 'app/components/shared/Panel';

// Grab guides from the file system (at ../../docs)
const GUIDES = ((guideRequire) =>
  guideRequire.keys().map((guidePath) =>
    guideRequire(guidePath).default
  )
)(require.context(
  '../../docs',
  true,
  /^\.\/[^/]+(?:\/index)?\.[^/]*$/ // matches any file in ../../docs, or any index file in a subdirectory of ../../docs
));

const getEmojiForGuide = ({ emoji, title }) => emoji || `:${title.toLowerCase()}:`;
const getSlugForGuide = ({ slug, title }) => slug || encodeURIComponent(title.toLowerCase());

class QuickStart extends React.PureComponent {
  static propTypes = {
    center: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      hash: PropTypes.string.isRequired,
      pathname: PropTypes.string.isRequired,
      search: PropTypes.string.isRequired
    }).isRequired,
    organization: PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      uuid: PropTypes.string,
      agentTokens: PropTypes.shape({
        edges: PropTypes.array.isRequired
      })
    }).isRequired,
    relay: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired
  };

  static defaultProps = {
    center: true,
    title: 'Agent Quick Start Guides'
  }

  componentDidMount() {
    this.props.relay.setVariables({
      isMounted: true
    });
  }

  getSlugFromHash() {
    const { location: { hash } } = this.props;

    if (hash.length < 2) {
      return null;
    }

    return hash.split('#setup-').pop() || null;
  }

  getIndexOfGuide() {
    const slugToFind = this.getSlugFromHash();

    if (!slugToFind) {
      return null;
    }

    const index = GUIDES.findIndex(
      (Guide) => getSlugForGuide(Guide) === slugToFind
    );

    if (index === -1) {
      return null;
    }

    return index;
  }

  renderGuideButtons(selectedGuideIndex) {
    const baseUri = `${this.props.location.pathname}${this.props.location.search}`;

    return (
      <div className="center" style={{ margin: -5 }}>
        {
          GUIDES.map((Guide, index) => {
            const slug = getSlugForGuide(Guide);

            return (
              <Link
                key={slug}
                to={
                  index === selectedGuideIndex
                    ? baseUri // use base route URI for selected guide so we can close it
                    : `${baseUri}#setup-${slug}`
                }
                className={classNames(
                  'inline-block semi-bold black hover-lime text-decoration-none border rounded m1 p1',
                  {
                    'border-white': index !== selectedGuideIndex,
                    'border-gray': index === selectedGuideIndex
                  }
                )}
              >
                <Emojify
                  className="block mt1"
                  style={{ fontSize: '1.15em' }}
                  text={getEmojiForGuide(Guide)}
                />
                {Guide.title}
              </Link>
            );
          })
        }
      </div>
    );
  }

  renderGuide(selectedGuideIndex) {
    const GuideToRender = GUIDES[selectedGuideIndex];
    const { name, slug, uuid, agentTokens: { edges: agentTokens } = {} } = this.props.organization;

    // Only show the token in the parameter table if we've got access to one
    let token;
    if (agentTokens && agentTokens.length && agentTokens[0].node.token) {
      token = agentTokens[0].node.token;
    }

    if (GuideToRender) {
      return (
        <GuideToRender
          className="border border-gray mt3"
          style={{
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: 'none'
          }}
          token={token}
          organization={{ name, slug, uuid }}
        />
      );
    }
  }

  render() {
    const selectedGuideIndex = this.getIndexOfGuide();
    const headerClassName = classNames({ center: this.props.center });

    return (
      <Panel className="mb3">
        <Panel.Header className={headerClassName}>
          {this.props.title}
        </Panel.Header>
        <Panel.Section>
          {this.renderGuideButtons(selectedGuideIndex)}
          {this.renderGuide(selectedGuideIndex)}
        </Panel.Section>
      </Panel>
    );
  }
}

export default Relay.createContainer(QuickStart, {
  initialVariables: {
    isMounted: false
  },

  fragments: {
    organization: () => Relay.QL`
      fragment on Organization {
        name
        slug
        uuid @include(if: $isMounted)
        agentTokens(first: 1, revoked: false) @include(if: $isMounted) {
          edges {
            node {
              token
            }
          }
        }
      }
    `
  }
});
